import { FC, useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, AlertCircle } from 'lucide-react';

import { ServiceFilters } from './ServiceFilters';
import { ServiceGrid } from './ServiceGrid';
import { ServiceList } from './ServiceList';
import { ServiceSortDropdown } from './ServiceSortDropdown';
import { ViewModeToggle } from './ViewModeToggle';
import { EmptyState } from './EmptyState';
import { Button } from '../../components/ui/Button';
import { ServicesGridSkeleton } from '../../components/skeletons';
import { useServices } from '../../hooks/useServices';
import { useServiceFilters } from '../../hooks/useServiceFilters';
import type {
  ServiceSortOption,
  ServiceFilters as ServiceFiltersType,
  ServiceCategory,
} from '../../types/service.types';

type ViewMode = 'grid' | 'list';

/**
 * Parse URL search params into initial filter/sort state
 */
function parseUrlParams(searchParams: URLSearchParams): {
  filters: Partial<ServiceFiltersType>;
  sortBy: ServiceSortOption;
  viewMode: ViewMode;
} {
  const category = searchParams.get('category');
  const search = searchParams.get('search');
  const sortBy = searchParams.get('sortBy') as ServiceSortOption | null;
  const viewMode = searchParams.get('viewMode') as ViewMode | null;

  const validSortOptions: ServiceSortOption[] = [
    'price-asc',
    'price-desc',
    'duration-asc',
    'duration-desc',
    'name-asc',
    'popular',
  ];

  const validCategories: Array<ServiceCategory | 'all'> = [
    'all',
    'Relaxation',
    'Therapeutic',
    'Sports',
    'Specialized',
  ];

  return {
    filters: {
      category: validCategories.includes(category as ServiceCategory | 'all')
        ? (category as ServiceCategory | 'all')
        : 'all',
      search: search || '',
    },
    sortBy: sortBy && validSortOptions.includes(sortBy) ? sortBy : 'popular',
    viewMode: viewMode === 'list' ? 'list' : 'grid',
  };
}

export const ServicesPage: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  // Parse initial state from URL
  const initialState = parseUrlParams(searchParams);

  // Initialize filter hook with URL state
  const { filters, sortBy, updateFilter, updateSort, clearFilters, hasActiveFilters } =
    useServiceFilters(initialState.filters, initialState.sortBy);

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>(initialState.viewMode);

  // Fetch services with current filters and sort
  const { data: services, isLoading, error, refetch } = useServices(filters, sortBy);

  // Sync state to URL
  useEffect(() => {
    const newParams = new URLSearchParams();

    if (filters.category && filters.category !== 'all') {
      newParams.set('category', filters.category);
    }
    if (filters.search) {
      newParams.set('search', filters.search);
    }
    if (sortBy !== 'popular') {
      newParams.set('sortBy', sortBy);
    }
    if (viewMode !== 'grid') {
      newParams.set('viewMode', viewMode);
    }

    setSearchParams(newParams, { replace: true });
  }, [filters.category, filters.search, sortBy, viewMode, setSearchParams]);

  // Handle filter changes - wrap for ServiceFilters component
  const handleFilterChange = useCallback(
    (newFilters: Partial<ServiceFiltersType>) => {
      Object.entries(newFilters).forEach(([key, value]) => {
        updateFilter(key as keyof ServiceFiltersType, value);
      });
    },
    [updateFilter]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSort: ServiceSortOption) => {
      updateSort(newSort);
    },
    [updateSort]
  );

  // Handle view mode change
  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  // Toggle mobile filters
  const toggleMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen((prev) => !prev);
  }, []);

  // Close mobile filters
  const closeMobileFilters = useCallback(() => {
    setIsMobileFiltersOpen(false);
  }, []);

  const resultCount = services?.length ?? 0;

  return (
    <>
      <Helmet>
        <title>Our Massage Therapy Services | Massatherapy</title>
        <meta
          name="description"
          content="Explore our range of professional massage therapy services including relaxation, therapeutic, sports, and specialized treatments. Book your appointment today."
        />
        <meta
          name="keywords"
          content="massage therapy, relaxation massage, sports massage, therapeutic massage, wellness, spa services"
        />
        <link rel="canonical" href="/services" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        {/* Page Header */}
        <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-100 sm:text-4xl">
              Our Massage Therapy Services
            </h1>
            <p className="mt-2 text-lg text-gray-600 dark:text-slate-400">
              Discover the perfect treatment for your wellness journey. From relaxation to
              rehabilitation, our expert therapists are here to help.
            </p>
          </div>
        </header>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:gap-8">
            {/* Desktop Sidebar - Filters */}
            <aside className="hidden lg:block lg:w-64 flex-shrink-0">
              <div className="sticky top-24">
                <ServiceFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  resultCount={resultCount}
                />
              </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Toolbar */}
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-lg bg-white dark:bg-slate-800 p-4 shadow-sm dark:shadow-slate-900/30">
                {/* Results Count */}
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  <span className="font-semibold text-gray-900 dark:text-slate-100">{resultCount}</span>{' '}
                  {resultCount === 1 ? 'service' : 'services'} found
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="ml-2 text-primary-600 hover:text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded"
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {/* Right Controls */}
                <div className="flex items-center gap-3">
                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleMobileFilters}
                    leftIcon={<SlidersHorizontal className="h-4 w-4" />}
                    className="lg:hidden"
                    aria-expanded={isMobileFiltersOpen}
                    aria-controls="mobile-filters"
                  >
                    Filters
                    {hasActiveFilters && (
                      <span className="ml-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-xs text-white">
                        !
                      </span>
                    )}
                  </Button>

                  {/* Sort Dropdown */}
                  <ServiceSortDropdown value={sortBy} onChange={handleSortChange} />

                  {/* View Mode Toggle */}
                  <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />
                </div>
              </div>

              {/* Mobile Filters Drawer */}
              {isMobileFiltersOpen && (
                <div
                  id="mobile-filters"
                  className="mb-6 lg:hidden"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Filter services"
                >
                  <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/30">
                    <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-4 py-3">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Filters</h2>
                      <button
                        type="button"
                        onClick={closeMobileFilters}
                        className="rounded-lg p-2 text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        aria-label="Close filters"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                    <div className="p-4">
                      <ServiceFilters
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={clearFilters}
                        resultCount={resultCount}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State - Skeleton Grid */}
              {isLoading && (
                <ServicesGridSkeleton viewMode={viewMode} count={6} animation="pulse" />
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div
                  className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6"
                  role="alert"
                  aria-live="assertive"
                >
                  <div className="flex items-start gap-3">
                    <AlertCircle
                      className="h-6 w-6 flex-shrink-0 text-red-600 dark:text-red-400"
                      aria-hidden="true"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">
                        Unable to load services
                      </h3>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                        {error.message || 'An unexpected error occurred. Please try again later.'}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => refetch()}
                        className="mt-4"
                      >
                        Try Again
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Services Display */}
              {!isLoading && !error && services && (
                <>
                  {services.length === 0 ? (
                    <EmptyState hasFilters={hasActiveFilters} onClearFilters={clearFilters} />
                  ) : viewMode === 'grid' ? (
                    <ServiceGrid services={services} />
                  ) : (
                    <ServiceList services={services} />
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ServicesPage;
